import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { http, resolveImage } from "@/lib/api";
import Layout from "@/components/Layout";
import { useSEO } from "@/hooks/useSEO";
import { ArrowLeft, ArrowRight, MapPin } from "lucide-react";

const C = {
  bg: "#070707", bg2: "#11100E", card: "#181614", paper: "#F5F2EB", text: "#F5F2EB", text2: "#B5B0A7",
  gold: "#C9A055", red: "#FF3B30", bSub: "rgba(245,242,235,0.10)", bGold: "rgba(201,160,85,0.35)",
};
const F = { brush: "'Permanent Marker', cursive", cond: "'Saira Condensed', sans-serif", hand: "'Caveat', cursive", sans: "'DM Sans', 'Outfit', sans-serif" };

function useVisible(threshold = 0.15) {
  const ref = useRef(null);
  const [v, setV] = useState(false);
  useEffect(() => {
    if (!ref.current) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setV(true); }, { threshold });
    obs.observe(ref.current);
    const timer = setTimeout(() => setV(true), 600);
    return () => { obs.disconnect(); clearTimeout(timer); };
  }, [threshold]);
  return [ref, v];
}

const MarkerStroke = ({ color = C.gold, width = "100%", height = "6px", className = "" }) => (
  <div className={className} style={{ width, height, background: color, borderRadius: "3px", transform: "rotate(-0.8deg)", opacity: 0.85, boxShadow: `0 0 12px ${color}40` }} />
);

export default function MCAbout() {
  useSEO({ title: "About the Artist — Marker Collection", description: "Meet Mickey Trivett — the East Tennessee artist behind the Marker Collection." });
  const navigate = useNavigate();
  const [pageInfo, setPageInfo] = useState({});
  const [loading, setLoading] = useState(true);
  const [heroRef, heroVis] = useVisible(0.1);
  const [storyRef, storyVis] = useVisible(0.1);
  const [valuesRef, valuesVis] = useVisible(0.1);

  const [show, setShow] = useState(false);
  useEffect(() => { if (!loading) { const t = setTimeout(() => setShow(true), 100); return () => clearTimeout(t); } }, [loading]);

  useEffect(() => {
    http.get("/marker-collection/public/pages").then(({ data }) => {
      setPageInfo(data?.about || {});
      if (data?.about?.enabled === false) navigate("/marker-collection");
    }).catch(() => {}).finally(() => setLoading(false));
  }, [navigate]);

  const valuePairs = (pageInfo?.values || "").split("|");
  const values = [];
  for (let i = 0; i < valuePairs.length - 1; i += 2) {
    values.push({ title: valuePairs[i], desc: valuePairs[i + 1] });
  }

  if (loading) return (
    <Layout><div className="min-h-screen flex items-center justify-center" style={{ background: C.bg }}>
      <div className="w-16 h-16 border-2 animate-spin" style={{ borderColor: `${C.gold}30`, borderTopColor: C.gold }} />
    </div></Layout>
  );

  return (
    <Layout>
      <div style={{ background: C.bg, color: C.text, minHeight: "100vh" }}>
        <style>{`
          .mc-grit::before { content:''; position:absolute; inset:0; width:100%; height:100%; background-image:url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.035'/%3E%3C/svg%3E"); pointer-events:none; z-index:0; }
        `}</style>

        {/* Hero */}
        <section ref={heroRef} className="relative mc-grit overflow-hidden" style={{ background: C.bg }} data-testid="mc-about-hero">
          <div className="max-w-7xl mx-auto px-6 py-14 md:py-20 relative z-10">
            <Link to="/marker-collection" className="inline-flex items-center gap-2 text-[10px] tracking-[0.25em] uppercase font-bold mb-6 hover:opacity-70 transition-opacity"
              style={{ fontFamily: F.sans, color: C.gold }} data-testid="mc-about-back">
              <ArrowLeft size={14} /> Back to Home
            </Link>
            <div className="grid lg:grid-cols-[auto_1fr] gap-10 lg:gap-16 items-center">
              {/* Photo */}
              {pageInfo?.image && (
                <div className={`transition-all duration-700 ${show || heroVis ? "opacity-100 scale-100" : "opacity-0 scale-90"}`}>
                  <div className="w-56 h-56 md:w-72 md:h-72 overflow-hidden flex-shrink-0 relative" style={{ border: `3px solid ${C.bGold}` }}>
                    <img src={resolveImage(pageInfo.image)} alt="Mickey Trivett" className="w-full h-full object-cover grayscale hover:grayscale-0 transition-all duration-700" />
                    <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-16 h-5 -rotate-2 opacity-40" style={{ background: C.paper }} />
                  </div>
                  <div className="flex items-center gap-2 mt-3 justify-center">
                    <MapPin size={12} style={{ color: C.gold }} />
                    <span className="text-[10px] tracking-[0.2em] uppercase" style={{ fontFamily: F.sans, color: C.text2 }}>East Tennessee</span>
                  </div>
                </div>
              )}
              {/* Title */}
              <div className={`transition-all duration-700 delay-200 ${show || heroVis ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
                <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black leading-[0.85] tracking-tight mb-4" style={{ fontFamily: F.cond, color: C.text }} data-testid="mc-about-title">
                  {pageInfo?.heading || "MEET THE GUY\nWITH THE MARKER."}
                </h1>
                <MarkerStroke width="80px" className="mb-5" />
                <p className="text-2xl sm:text-3xl leading-snug italic" style={{ fontFamily: F.hand, color: `${C.gold}60` }}>
                  "{pageInfo?.quote || "Same marker. Different stories. Always a good time."}"
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Story */}
        <section ref={storyRef} className="relative mc-grit" style={{ background: C.bg2, borderTop: `1px solid ${C.bSub}`, borderBottom: `1px solid ${C.bSub}` }} data-testid="mc-about-story">
          <div className="max-w-3xl mx-auto px-6 py-14 md:py-20 relative z-10">
            <div className={`transition-all duration-700 ${show || storyVis ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
              {(pageInfo?.text || "").split("\n").filter(Boolean).map((para, i) => (
                <p key={i} className="text-base leading-relaxed mb-5" style={{ fontFamily: F.sans, color: C.text2 }}>{para}</p>
              ))}
            </div>
          </div>
        </section>

        {/* Values */}
        {values.length > 0 && (
          <section ref={valuesRef} className="relative mc-grit" style={{ background: C.bg }} data-testid="mc-about-values">
            <div className="max-w-7xl mx-auto px-6 py-14 md:py-20 relative z-10">
              <h2 className={`text-3xl sm:text-4xl font-black tracking-tight mb-10 text-center transition-all duration-700 ${show || valuesVis ? "opacity-100" : "opacity-0"}`}
                style={{ fontFamily: F.cond, color: C.text }}>WHAT I BELIEVE</h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                {values.map((v, i) => (
                  <div key={i} className={`p-6 text-center transition-all duration-600 ${show || valuesVis ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
                    style={{ transitionDelay: `${150 + i * 120}ms`, background: `${C.gold}05`, border: `1px solid ${C.bGold}` }}>
                    <h3 className="text-sm font-bold tracking-[0.15em] uppercase mb-3" style={{ fontFamily: F.sans, color: C.gold }}>{v.title}</h3>
                    <p className="text-xs leading-relaxed" style={{ fontFamily: F.sans, color: C.text2 }}>{v.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Closing */}
        <section className="relative mc-grit" style={{ background: C.bg2, borderTop: `1px solid ${C.bSub}` }} data-testid="mc-about-closing">
          <div className="max-w-7xl mx-auto px-6 py-14 relative z-10 text-center">
            <p className="text-2xl sm:text-3xl font-black tracking-tight leading-tight mb-6" style={{ fontFamily: F.cond, color: C.text }}>
              LIVE LIFE. DO GOOD. BE HAPPY.
            </p>
            <MarkerStroke width="100px" className="mx-auto mb-6" />
            <div className="flex flex-wrap gap-4 justify-center">
              <Link to="/marker-collection/gallery" className="inline-flex items-center gap-3 px-7 py-3.5 text-xs tracking-[0.25em] uppercase font-bold transition-all hover:gap-5"
                style={{ fontFamily: F.sans, background: C.gold, color: C.bg }} data-testid="mc-about-gallery-cta">
                VIEW THE COLLECTION <ArrowRight size={14} />
              </Link>
              <Link to="/marker-collection/commissions" className="inline-flex items-center gap-3 px-7 py-3.5 text-xs tracking-[0.25em] uppercase font-bold border-2 transition-all hover:bg-white/5"
                style={{ fontFamily: F.sans, borderColor: C.text, color: C.text }} data-testid="mc-about-commission-cta">
                REQUEST A COMMISSION
              </Link>
            </div>
          </div>
        </section>
      </div>
    </Layout>
  );
}
